/**
 * Station (connected client), job-history and CloudEvent fixtures.
 *
 * Same determinism rules as `devices.ts`. Station MACs/IPs and job ids use the
 * wire formats (`AA:BB:…`, `job:…`) so the table components are exercised with
 * real shapes, including the LTR-inside-RTL bidi case.
 */

import type { CloudEvent, DeviceJobRow, JobStatus, StationItem } from '../api/types';
import { eventTypeUri } from '../api/types';
import { FIXTURE_DEVICES, isoAgo, stationCount, type FixtureDevice } from './devices';

export const EVENT_SOURCE = 'https://acs.example/integration/v1';

function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h = Math.imul(h ^ value.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const HOST_NAMES = [
  'PHONE-A512',
  'LAPTOP-RAHIMI',
  'IPHONE-MINA',
  'TV-SALON-4K',
  'TABLET-SARA',
  'PC-WORK-01',
  'IOT-PLUG-07',
  'PHONE-KAMRAN',
];

export function stationsFor(device: FixtureDevice): StationItem[] {
  const r = mulberry32(hashString(device.serial));
  const count = stationCount(device);
  const out: StationItem[] = [];
  for (let i = 0; i < count; i += 1) {
    const macSeed = `${device.serial}:sta${i}`;
    const h = mulberry32(hashString(macSeed));
    const bytes: string[] = [];
    for (let b = 0; b < 6; b += 1) {
      bytes.push(
        Math.floor(h() * 254 + 1)
          .toString(16)
          .padStart(2, '0')
          .toUpperCase(),
      );
    }
    const host = HOST_NAMES[(i + count) % HOST_NAMES.length] ?? 'UNKNOWN-HOST';
    out.push({
      mac: bytes.join(':'),
      ip: `192.168.1.${20 + i * 3}`,
      hostname: host,
      ssid_instance: device.wifiBand === '5g' && i % 3 === 2 ? 2 : 1,
      seen_at: isoAgo(Math.floor(r() * 40) + 1),
    });
  }
  return out;
}

interface JobSeed {
  action: string;
  status: JobStatus;
  summary: string | null;
  ago: number;
  durationMin: number;
  errorCode?: DeviceJobRow['error_code'];
}

/**
 * Per-device job history. The narrative is fixed for the first few devices so
 * the UI always has a failure, an in-flight job and a policy apply to show.
 */
const JOB_STORIES: Record<string, JobSeed[]> = {
  rebootOk: [
    {
      action: 'read_snapshot',
      status: 'completed',
      summary: '۴۲ پارامتر تازه‌سازی شد',
      ago: 34,
      durationMin: 1,
    },
    {
      action: 'reboot',
      status: 'completed',
      summary: 'دستگاه با موفقیت ری‌استارت شد',
      ago: 190,
      durationMin: 3,
    },
    {
      action: 'apply_policy',
      status: 'completed',
      summary: 'سیاست ftth-ppoe-100m اعمال شد',
      ago: 1_520,
      durationMin: 6,
    },
  ],
  failing: [
    {
      action: 'reboot',
      status: 'failed',
      summary: 'connection request بی‌پاسخ ماند',
      ago: 96,
      durationMin: 2,
      errorCode: 'device-offline',
    },
    {
      action: 'read_snapshot',
      status: 'failed',
      summary: 'دستگاه آفلاین است',
      ago: 101,
      durationMin: 1,
      errorCode: 'device-offline',
    },
    { action: 'apply_policy', status: 'queued', summary: null, ago: 8, durationMin: 0 },
  ],
  running: [
    {
      action: 'apply_policy',
      status: 'running',
      summary: 'در حال اعمال suspend…',
      ago: 2,
      durationMin: 0,
    },
    {
      action: 'rotate_credential',
      status: 'completed',
      summary: 'رمز وای‌فای چرخش یافت',
      ago: 430,
      durationMin: 4,
    },
  ],
  speedtest: [
    // A probe's history line names the object it was read from and the board's own word for the
    // result (§۵.۸): a `۹۱۲Mbps` figure here would be a number this service never computed.
    {
      action: 'diagnostics:speedtest',
      status: 'completed',
      summary: 'DownloadDiagnostics خوانده شد — TotalBytesReceived',
      ago: 55,
      durationMin: 2,
    },
    {
      action: 'diagnostics:ping',
      status: 'completed',
      summary: 'IPPingDiagnostics: SuccessCount ۸ از ۸، AverageResponseTime ۱۹ms',
      ago: 58,
      durationMin: 1,
    },
    {
      action: 'read_snapshot',
      status: 'completed',
      summary: '۴۲ پارامتر تازه‌سازی شد',
      ago: 72,
      durationMin: 1,
    },
  ],
};

function storyKey(index: number): keyof typeof JOB_STORIES {
  if (index === 2 || index === 3) return 'failing';
  if (index === 0 || index === 5) return 'rebootOk';
  if (index % 7 === 4) return 'speedtest';
  if (index % 3 === 1) return 'running';
  return 'rebootOk';
}

export function jobsFor(device: FixtureDevice, index: number): DeviceJobRow[] {
  const key = storyKey(index);
  const seeds = JOB_STORIES[key] ?? [];
  return seeds.map((seed, i) => {
    const requested = isoAgo(seed.ago);
    const row: DeviceJobRow = {
      job_id: `job:${hashString(`${device.serial}${key}${i}`).toString(16).padStart(12, '0').slice(0, 12)}`,
      action: seed.action,
      status: seed.status,
      requested_at: requested,
      started_at: seed.status === 'queued' ? null : requested,
      completed_at:
        seed.status === 'completed' || seed.status === 'failed'
          ? isoAgo(Math.max(0, seed.ago - seed.durationMin))
          : null,
      result_summary: seed.summary,
    };
    if (seed.errorCode) row.error_code = seed.errorCode;
    return row;
  });
}

type Kind =
  | 'device.discovered'
  | 'device.bound'
  | 'device.inform'
  | 'device.online_status'
  | 'device.parameter.changed'
  | 'device.command.completed'
  | 'device.config.drift'
  | 'onu.los'
  | 'optical.degraded'
  | 'campaign.progress'
  | 'provision.completed'
  | 'diagnostics.completed'
  | 'reconciliation.mismatch';

interface EventSeed {
  kind: Kind;
  deviceOffset: number;
  ago: number;
  data: Record<string, unknown>;
}

/**
 * Insertion-ordered outbox, exactly like `GET /integration/v1/events` (the
 * façade replays in enqueue order; the dashboard re-sorts for display).
 */
const EVENT_SEEDS: EventSeed[] = [
  {
    kind: 'device.inform',
    deviceOffset: 0,
    ago: 224,
    data: { event: '0 BOOTSTRAP', interval: 300 },
  },
  {
    kind: 'device.discovered',
    deviceOffset: 4,
    ago: 214,
    data: { serial: '', oui: '43444154', product_class: 'GPT-741X' },
  },
  { kind: 'device.bound', deviceOffset: 4, ago: 210, data: { nas_ref: '' } },
  {
    kind: 'optical.degraded',
    deviceOffset: 5,
    ago: 205,
    data: {
      olt_ref: 'olt-tehran-02',
      pon_port: 'PON-0/4',
      rx_dbm: -27.8,
      tx_dbm: 2.2,
      threshold_dbm: -27,
      sample_count: 12,
      sampled_at: isoAgo(205),
    },
  },
  {
    kind: 'device.parameter.changed',
    deviceOffset: 1,
    ago: 190,
    data: { path: 'Device.WiFi.SSID.1.SSID', from: 'TSC_OLD', to: 'TSC_NEW' },
  },
  {
    kind: 'device.command.completed',
    deviceOffset: 0,
    ago: 188,
    data: { command: 'reboot', outcome: 'ok' },
  },
  {
    kind: 'device.config.drift',
    deviceOffset: 6,
    ago: 150,
    data: {
      policy_ref: 'policy:ftth-ppoe-100m',
      paths: ['Device.ManagementServer.PeriodicInformInterval'],
    },
  },
  {
    kind: 'device.online_status',
    deviceOffset: 2,
    ago: 100,
    data: { online: false, last_seen_at: isoAgo(100) },
  },
  {
    kind: 'onu.los',
    deviceOffset: 2,
    ago: 98,
    data: {
      olt_ref: 'olt-karaj-01',
      pon_port: 'PON-0/2',
      alarm: 'LOS',
      severity: 'critical',
      cleared: false,
      occurred_at: isoAgo(98),
    },
  },
  {
    kind: 'provision.completed',
    deviceOffset: 6,
    ago: 80,
    data: { policy_ref: 'policy:ftth-ppoe-100m', steps: 7, outcome: 'ok' },
  },
  // The published `diagnostics.completed` shape (schemas/cloudevents/): the seven keys the consumer
  // codes against, with the probe's answer inside `result.observed` under the object path the board
  // answered from — `outcome` is the board's own `DiagnosticsState` word, not a verdict the façade
  // invented, and there is no `rtt_avg_ms` because nothing here computes an average.
  {
    kind: 'diagnostics.completed',
    deviceOffset: 1,
    ago: 58,
    data: {
      diagnostics_id: 'job:fxdiag0001TSC0',
      job_id: 'job:fxdiag0001TSC0',
      action: 'diagnostics:ping',
      outcome: 'Complete',
      target: '192.0.2.10',
      ticket_ref: null,
      result: {
        task: 'diagnostics',
        probe: 'ping',
        target: '192.0.2.10',
        nbi_task: { _id: 'task:fxdiag0001', name: 'ping' },
        read_from: 'InternetGatewayDevice.IPPingDiagnostics.',
        observed: {
          'InternetGatewayDevice.IPPingDiagnostics.DiagnosticsState': 'Complete',
          'InternetGatewayDevice.IPPingDiagnostics.Host': '192.0.2.10',
          'InternetGatewayDevice.IPPingDiagnostics.SuccessCount': 8,
          'InternetGatewayDevice.IPPingDiagnostics.FailureCount': 0,
          'InternetGatewayDevice.IPPingDiagnostics.AverageResponseTime': 19,
        },
        outcome: 'Complete',
        read_at: isoAgo(58),
      },
    },
  },
  {
    kind: 'onu.los',
    deviceOffset: 7,
    ago: 46,
    data: {
      olt_ref: 'olt-tehran-01',
      pon_port: 'PON-0/1',
      alarm: 'LOS',
      severity: 'critical',
      cleared: false,
      occurred_at: isoAgo(46),
    },
  },
  {
    kind: 'optical.degraded',
    deviceOffset: 0,
    ago: 41,
    data: {
      olt_ref: 'olt-tehran-01',
      pon_port: 'PON-0/2',
      rx_dbm: -25.6,
      tx_dbm: 2.9,
      threshold_dbm: -25,
      sample_count: 6,
      sampled_at: isoAgo(41),
    },
  },
  {
    kind: 'campaign.progress',
    deviceOffset: 7,
    ago: 44,
    data: {
      campaign_id: 'cmp-hw-8546-canary',
      wave: 'canary',
      phase: 'download',
      from_version: 'V3R020C10.118',
      to_version: 'V3R020C10.126',
      outcome: 'running',
      devices_total: 24,
      devices_succeeded: 18,
      devices_failed: 1,
      error_rate: 0.042,
    },
  },
  {
    kind: 'device.online_status',
    deviceOffset: 3,
    ago: 27,
    data: { online: false, last_seen_at: isoAgo(27) },
  },
  {
    kind: 'reconciliation.mismatch',
    deviceOffset: 3,
    ago: 30,
    data: { olt_ref: 'olt-qom-01', kind: 'authorized_without_binding', action: 'review' },
  },
  {
    kind: 'reconciliation.mismatch',
    deviceOffset: 1,
    ago: 9,
    data: { olt_ref: 'olt-karaj-01', kind: 'binding_without_onu', action: 'review' },
  },
  { kind: 'device.inform', deviceOffset: 1, ago: 12, data: { event: '2 PERIODIC', interval: 300 } },
  {
    kind: 'device.online_status',
    deviceOffset: 3,
    ago: 6,
    data: { online: true, last_seen_at: isoAgo(6) },
  },
  { kind: 'device.inform', deviceOffset: 0, ago: 2, data: { event: '2 PERIODIC', interval: 300 } },
];

export const FIXTURE_EVENTS: readonly CloudEvent[] = EVENT_SEEDS.map((seed, i) => {
  const device = FIXTURE_DEVICES[seed.deviceOffset % FIXTURE_DEVICES.length];
  if (!device) throw new Error('fixture event without device');
  const data: Record<string, unknown> = {
    acs_device_id: device.acs_device_id,
    ...seed.data,
  };
  if (seed.kind === 'device.discovered') {
    data.serial = device.serial;
  }
  if (seed.kind === 'device.bound') {
    data.nas_ref = device.nas_ref ?? `nas:pending-${i}`;
  }
  return {
    specversion: '1.0',
    id: `evt:${hashString(`${seed.kind}${i}`).toString(16).padStart(20, '0').slice(0, 20)}`,
    source: EVENT_SOURCE,
    type: eventTypeUri(seed.kind),
    time: isoAgo(seed.ago),
    datacontenttype: 'application/json',
    data,
  } satisfies CloudEvent;
});
